import { createSelector } from 'reselect'
import { Map, List, fromJS } from 'immutable'
import { compose, identity, isNil } from 'ramda'
import {
  get,
  getIn,
  pickToArray,
  pick,
  map,
  keys,
  values,
  filter,
  filterNot,
  pluck,
  valueSeq,
  differenceWith,
  safeToJS,
  getInOr,
  getEq,
  getOr,
} from 'utils/immutable'
import { tempIdEntity } from 'utils/identity'
import { createToJsSelector } from 'selectors/utils'

const modulesSelector = getIn(['ecp', 'module'])
const linkedLogicsSelector = getIn(['ecp', 'linkedLogics'])

const moduleSlotListSelector = (state, props) =>
  state.getIn(['ecp', 'module', props.id, 'slots'])

const slotsSelector = getIn(['ecp', 'slots'])

const solenoidsSelector = getIn(['ecp', 'solenoid'])

const modificationCountSelector = (state) =>
  state.getIn(['ecp', 'modificationCount']) || 0

export const getModificationCount = modificationCountSelector

const moduleSolenoidSelector = getIn(['ecp', 'moduleSolenoids'])

const solenoidSelector = (state, props) =>
  state.getIn(['ecp', 'solenoid', props.id])

const moduleSolenoidByIdSelector = (state, props) =>
  state.getIn(['ecp', 'moduleSolenoids', props.id])

const getEcpModuleKeys = createSelector(modulesSelector, keys)
export const ecpModulesSelector = createToJsSelector(getEcpModuleKeys)

const moduleSlotSelector = createSelector(
  moduleSlotListSelector,
  slotsSelector,
  pickToArray
)

const getEcpModuleValues = createSelector(modulesSelector, values)
export const getECPVariantForVehicle = createSelector(
  getEcpModuleValues,
  (ecpModules) => {
    if (ecpModules.isEmpty()) return null
    // ecuVariant for ecus should be the same. If not, that is a data issue.
    const ecpVariant = ecpModules.first().get('ecuVariant')
    return ecpVariant
  }
)

const ecpForUpdateSelector = createSelector(
  moduleSlotListSelector,
  slotsSelector,
  solenoidsSelector,
  (moduleSlotList, slots, solenoids) => {
    const solenoidIds = getSolenoidIds('solenoid', slots)

    return Map({
      input: moduleSlotList,
      solenoid: pick(solenoidIds, solenoids),
      slots: pick(moduleSlotList, slots).map((item) =>
        item.set('solenoid', tempIdEntity(item.get('solenoid')))
      ),
    })
  }
)

export const ecpForUpdate = createToJsSelector(ecpForUpdateSelector)

export const makeEcpModuleSlotsSelector = () =>
  createToJsSelector(moduleSlotSelector)

export const makeEcpModuleSolenoidSelector = () =>
  createToJsSelector(
    createSelector(
      moduleSlotListSelector,
      slotsSelector,
      solenoidsSelector,
      moduleSolenoidSelector,
      (moduleSlotList, slots, solenoids, moduleSolenoids) => {
        // Prefer vehicle-response solenoid data (moduleSolenoids) which contains
        // factory-overridden collet colors from IMS/SGLSPLOC. Fall back to catalog
        // data (solenoids) only for aftermarket solenoids absent from the vehicle response.
        const safeModuleSolenoids = moduleSolenoids || Map()
        const getSolenoid = (id) => {
          if (!id) return Map()
          const vehicleSolenoid = safeModuleSolenoids.get(tempIdEntity(id))
          if (vehicleSolenoid) return vehicleSolenoid
          return solenoids.get(tempIdEntity(id)) || Map()
        }
        const solenoidOf = compose(getSolenoid, get('solenoid'))
        const mergeSolenoid = (slot) => slot.merge(solenoidOf(slot))
        return compose(map(mergeSolenoid), pickToArray(moduleSlotList))(slots)
      }
    )
  )

export const getAllSolenoids = createToJsSelector(
  createSelector(solenoidsSelector, (solenoids) => solenoids)
)
export const getAllSlots = createToJsSelector(
  createSelector(slotsSelector, (slots) => slots)
)

export const getOccupiedSlotSolenoids = createToJsSelector(
  createSelector(
    slotsSelector,
    solenoidsSelector,
    moduleSolenoidSelector,
    (slots, solenoids, moduleSolenoids) => {
      const safeModuleSolenoids = moduleSolenoids || Map()
      return slots
        .filter((slot) => slot.get('solenoid'))
        .map((slot) => {
          const id = tempIdEntity(slot.get('solenoid'))
          return safeModuleSolenoids.get(id) || solenoids.get(id) || Map()
        })
        .filter((s) => s && s.size > 0)
    }
  )
)

export const makeSolenoidSelector = () =>
  createToJsSelector(createSelector(solenoidSelector, identity))

// Prefer vehicle-response solenoid data when available (contains correct factory
// collet colors). Fall back to catalog data for solenoids absent from the vehicle response.
const solenoidsByIdSelector = createSelector(
  solenoidSelector,
  moduleSolenoidByIdSelector,
  (solenoid, moduleSolenoid) => {
    if (moduleSolenoid) return moduleSolenoid
    return solenoid
  }
)
export const makeSolenoidsByIdSelector = () =>
  createToJsSelector(solenoidsByIdSelector)

export const getSolenoidIds = (prop, slots) =>
  compose(
    filter((item) => item !== undefined),
    valueSeq,
    pluck(prop)
  )(slots)

const getSlotsWithSolenoids = (prop, slots, solenoidIds) =>
  compose(
    filter((slot) => solenoidIds.includes(slot.get(prop)) || slot.get(prop)),
    valueSeq
  )(slots)

const currentSolenoidsSelector = createSelector(
  slotsSelector,
  solenoidsSelector,
  (slots, solenoids) => {
    if (solenoids.isEmpty()) return List()
    const solenoidIds = getSolenoidIds('solenoid', slots).filter(
      (id) => !isNil(id)
    )
    const slotsWithSolenoids = getSlotsWithSolenoids(
      'solenoid',
      slots,
      solenoidIds
    )
    const currentSolenoids = slotsWithSolenoids.reduce((acc, currSlot) => {
      const location = currSlot.get('location')
      const solenoidId = currSlot.get('solenoid')
      if (isNil(solenoidId)) {
        return acc
      }
      const solenoid = solenoids.get(solenoidId)
      const locationMap = new Map({
        location,
      })

      const updatedSolenoid = solenoid.merge(locationMap)
      return acc.push(updatedSolenoid)
    }, List())
    return currentSolenoids
  }
)

export const getCurrentSolenoids = createToJsSelector(
  createSelector(
    currentSolenoidsSelector,
    (currentSolenoids) => currentSolenoids
  )
)

const originalSolenoidsSelector = createSelector(
  slotsSelector,
  solenoidsSelector,
  (slots, solenoids) => {
    if (solenoids.isEmpty()) return List()
    const solenoidIds = getSolenoidIds('originalSolenoid', slots).filter(
      (id) => !isNil(id)
    )
    const slotsWithSolenoids = getSlotsWithSolenoids(
      'originalSolenoid',
      slots,
      solenoidIds
    )
    const originalSolenoids = slotsWithSolenoids.reduce((acc, currSlot) => {
      const location = currSlot.get('location')
      const solenoidId = currSlot.get('originalSolenoid')
      const solenoid = solenoids.get(solenoidId)
      const locationMap = new Map({
        location,
      })
      const updatedSolenoid = solenoid.merge(locationMap)
      return acc.push(updatedSolenoid)
    }, List())
    return originalSolenoids
  }
)

const cmp = (x, y) => x.get('uri') === y.get('uri')
const addedSolenoidsSelector = createSelector(
  currentSolenoidsSelector,
  originalSolenoidsSelector,
  (currentSolenoids, originalSolenoids) =>
    differenceWith(cmp, currentSolenoids, originalSolenoids)
)

export const addedSolenoids = createToJsSelector(addedSolenoidsSelector)

const removedSolenoidsSelector = createSelector(
  currentSolenoidsSelector,
  originalSolenoidsSelector,
  (currentSolenoids, originalSolenoids) =>
    differenceWith(cmp, originalSolenoids, currentSolenoids)
)

export const removedSolenoids = createToJsSelector(removedSolenoidsSelector)

const movedSolenoidsSelector = createSelector(
  slotsSelector,
  solenoidsSelector,
  originalSolenoidsSelector,
  (slots, solenoids, originalSolenoids) => {
    const updatedSlots = slots
      .filter(
        (slot) =>
          slot.get('solenoid') &&
          slot.get('solenoid') !== slot.get('originalSolenoid') &&
          originalSolenoids.some(
            (item) => item.get('functionId') === slot.get('solenoid')
          )
      )
      .valueSeq()
    const ids = map(get('solenoid'))
    return pickToArray(ids(updatedSlots), solenoids)
  }
)

export const movedSolenoids = createToJsSelector(movedSolenoidsSelector)

export const makeNumSlotsOccupiedSelector = () =>
  createSelector(moduleSlotSelector, (slots) =>
    slots.valueSeq().reduce((acc, slot) => {
      if (slot.get('solenoid')) return acc + 1
      return acc
    }, 0)
  )

export const solenoidListSelector = createToJsSelector(
  createSelector(
    solenoidsSelector,
    slotsSelector,
    (__, props) => props.currentLocation,
    (solenoids, slots, currentLocation) => {
      const usedSolenoidIds = getSolenoidIds('solenoid', slots)
      const filterUsedSolenoids = (item) =>
        usedSolenoidIds.includes(item.get('functionId'))
      const filterValidCondition = (item) =>
        item.get('allowedLocations').includes(currentLocation)
      const sortedSolenoids = solenoids.sort((a, b) =>
        a.get('function').localeCompare(b.get('function'))
      )
      const validOptions = new Map({
        children: compose(
          filterNot(filterUsedSolenoids),
          filter(filterValidCondition),
          valueSeq
        )(sortedSolenoids),
        groupName: 'Valid Solenoids',
      })
      const invalidOptions = new Map({
        children: compose(
          filterNot(filterUsedSolenoids),
          filterNot(filterValidCondition),
          map((item) => item.set('disabled', true)),
          valueSeq
        )(sortedSolenoids),
        groupName: 'Invalid Solenoids',
      })
      const optionsList = new List([validOptions, invalidOptions])
      return optionsList
    }
  )
)

// convert pending slots data to pending slots per ecp
const getEcpFromSlotUri = (slot) =>
  slot.get('uri').split('/').slice(5, 6).toString()
const getPendingSlots = createSelector(
  slotsSelector,
  solenoidsSelector,
  modificationCountSelector,
  (slots, solenoids, modificationCount) => {
    if (!slots) return List()
    // Filter slots that have changes: changeCount > 0, pending flag, or solenoid !== originalSolenoid
    // changeCount tracks cumulative changes (add/remove operations) that only go up
    const pendingSlots = slots.filter((slot) => {
      // Skip partial slots that don't have uri (created during editPipeline for non-existent slots)
      // These can be created when removeSolenoid shifts solenoids through slots that weren't in the API response
      if (!slot.get('uri')) return false

      const hasChangeCount = (slot.get('changeCount') || 0) > 0
      const hasPendingFlag = !(
        getEq('pending', false, slot) || getEq('pending', undefined, slot)
      )
      const hasLocalChange =
        slot.get('solenoid') !== slot.get('originalSolenoid')

      return hasChangeCount || hasPendingFlag || hasLocalChange
    })

    // Helper to get solenoid name from ID
    const getSolenoidName = (solenoidId) => {
      if (!solenoidId) return null
      const solenoid = solenoids.get(tempIdEntity(solenoidId))
      return solenoid ? solenoid.get('function') : null
    }

    const transformedPendingSlotData = pendingSlots
      .valueSeq()
      .reduce((acc, currentSlot) => {
        const ecpId = getEcpFromSlotUri(currentSlot)
        const ecpAccById = acc.get(ecpId)
        if (!isNil(ecpId)) {
          // Collect unique solenoid names from both current and original (for adds and removes)
          const currentSolenoidId = currentSlot.get('solenoid')
          const originalSolenoidId = currentSlot.get('originalSolenoid')
          const currentName = getSolenoidName(currentSolenoidId)
          const originalName = getSolenoidName(originalSolenoidId)

          // Get existing parts set or create new one
          const existingParts = ecpAccById
            ? ecpAccById.get('parts')
            : new List([])

          // Add both current and original solenoid names (if they exist and are different)
          let updatedParts = existingParts
          if (currentName && !existingParts.includes(currentName)) {
            updatedParts = updatedParts.push(currentName)
          }
          if (
            originalName &&
            originalName !== currentName &&
            !updatedParts.includes(originalName)
          ) {
            updatedParts = updatedParts.push(originalName)
          }

          const updatedSlots = ecpAccById
            ? ecpAccById.get('slots').push(currentSlot)
            : new List([currentSlot])

          // Use actual slot count per ECP module for accurate per-module count
          // This ensures badge count matches the expanded item count
          const pendingCount = updatedSlots.size

          const pendingSlotMap = fromJS({
            [ecpId]: {
              ecpId,
              pendingCount,
              slots: updatedSlots,
              parts: updatedParts,
            },
          })
          return acc.merge(pendingSlotMap)
        }
        return acc
      }, Map())
    return transformedPendingSlotData.valueSeq()
  }
)

export const makeGetPendingModuleSlotSelector =
  createToJsSelector(getPendingSlots)

export const hasModifications = createSelector(
  addedSolenoidsSelector,
  removedSolenoidsSelector,
  movedSolenoidsSelector,
  (added, removed, moved) =>
    !added.isEmpty() || !removed.isEmpty() || !moved.isEmpty()
)

/**
 * Get the IDs of modules that have modifications.
 * Examines each slot and checks if solenoid !== originalSolenoid.
 * Extracts module ID from slot location (e.g., SB201 -> module 2).
 * @returns {string[]} Array of module IDs with modifications
 */
const modifiedModuleIdsSelector = createSelector(slotsSelector, (slots) => {
  if (!slots) return List()
  const modifiedModuleIds = new Set()
  slots.forEach((slot) => {
    if (slot.get('solenoid') !== slot.get('originalSolenoid')) {
      const location = slot.get('location')
      if (location && location.length >= 3) {
        // Location format is SBxxx where x is module ID (1, 2, or 3)
        const moduleId = location.charAt(2)
        modifiedModuleIds.add(moduleId)
      }
    }
  })
  return List(Array.from(modifiedModuleIds))
})

export const getModifiedModuleIds = createToJsSelector(
  modifiedModuleIdsSelector
)

const draggingItemSelector = (state) =>
  getInOr(Map(), ['ecp', 'draggingSolenoidItem'], state)

export const getDraggingItem = createSelector(draggingItemSelector, safeToJS)
export const getIsAllowedDropLocation = createSelector(
  draggingItemSelector,
  (_, props) => props.location,
  (draggingSolenoidItem, location) => {
    // Block drops to ECP 3 locations
    const targetModuleId = getModuleIdFromLocation(location)
    if (targetModuleId === '3') {
      return false
    }

    const allowedDropLocations = getInOr(
      List(),
      ['solenoid', 'allowedLocations'],
      draggingSolenoidItem
    )
    const hasAllowedDropLocation = allowedDropLocations.some(
      (allowedDropLocation) => allowedDropLocation === location
    )
    return hasAllowedDropLocation
  }
)

const selectIsDragging = (state) => getInOr(false, ['ecp', 'isDragging'], state)
export const getIsDragging = createSelector(selectIsDragging, safeToJS)

/**
 * Extract module ID from location string (e.g., "SB301" -> "3")
 */
export const getModuleIdFromLocation = (location) =>
  location && location.length >= 3 ? location.charAt(2) : null

/**
 * Check if the currently dragged solenoid is from a locked ECP (ECP 3)
 */
export const getIsDraggingFromLockedEcp = createSelector(
  draggingItemSelector,
  (draggingSolenoidItem) => {
    const location = getInOr(
      null,
      ['solenoid', 'location'],
      draggingSolenoidItem
    )
    return getModuleIdFromLocation(location) === '3'
  }
)

/**
 * Check if a drop target location is on ECP 3 (locked)
 */
export const getIsLockedDropLocation = createSelector(
  selectIsDragging,
  (_, props) => props.location,
  (isDragging, location) => {
    if (!isDragging) return false
    const targetModuleId = getModuleIdFromLocation(location)
    return targetModuleId === '3'
  }
)

const ecpLinkedLogicsSelector = createSelector(
  linkedLogicsSelector,
  (_, props) => props,
  (linkedLogics, { outputId, moduleType }) => {
    const linkedItems = getOr(List(), `${moduleType}-${outputId}`, linkedLogics)
    return linkedItems.map((item) => {
      const values = pick(
        ['partNumber', 'uri', 'description', 'revision', 'outputDescription'],
        item
      )
      return values.set('id', values.get('partNumber'))
    })
  }
)

/**
 * Get the linked logics (features) associated with a solenoid.
 * @param {{ string: outputId, string: moduleType }}
 * @returns {object[]}
 */
export const getEcpLinkedLogics = createSelector(
  ecpLinkedLogicsSelector,
  safeToJS
)

/**
 * Returns the Feature Output description for a solenoid, or null if not loaded / not assigned.
 * Used by canvas, side panel list, and detail pane to display the output name.
 * @param {object} state
 * @param {{ outputId: string|null, moduleType: string }} props
 * @returns {string|null}
 */
export const getOutputDescriptionForSolenoid = (
  state,
  { outputId, moduleType }
) => {
  if (!outputId || !moduleType) return null
  const linkedLogics = getIn(['ecp', 'linkedLogics'])(state)
  if (!linkedLogics) return null
  const features = linkedLogics.get(`${moduleType}-${outputId}`)
  if (!features || features.isEmpty()) return null
  const sorted = features.sortBy((f) => f.get('partNumber'))
  return sorted.first().get('outputDescription') || null
}

export const solenoidListOnVehicleSelector = createToJsSelector(
  createSelector(slotsSelector, (slots) => {
    const solenoidIds = getSolenoidIds('solenoid', slots)
    return solenoidIds
  })
)

export const availableSlotsOnVehicleForSolenoidsSelector = createSelector(
  slotsSelector,
  (slots) =>
    slots
      .toIndexedSeq()
      .toArray()
      .filter((item) => !item.get('solenoid'))
      .map((item) => item.get('location'))
)

// Stable reference to prevent infinite re-renders when optionalSolenoids is null.
// Using [] inline would create a new array reference on each selector call,
// breaking memoization and causing useEffect loops in consuming components.
const EMPTY_ARRAY = []

const optionalSolenoidsSelector = (state) =>
  getInOr(EMPTY_ARRAY, ['ecp', 'optionalSolenoids'], state)

export const getOptionalSolenoids = createSelector(
  optionalSolenoidsSelector,
  (optionalSolenoids) => optionalSolenoids
)

export const getEcpActiveSerialNumber = (state) =>
  state.getIn(['ecp', 'activeSerialNumber']) || null
